/**
 * How recently a connection must have synced for the settings UI to call it healthy.
 *
 * Deliberately generous. Most feeds are event-driven rather than continuous — Thrive mails a
 * credit memo only when NetSuite issues one, AWG cuts checks roughly weekly, C&S posts on a
 * weekly window — so a quiet week is normal operation, not a broken connection. Ten days clears
 * every cadence we schedule plus a long holiday weekend, which means a muted card only when
 * something really has stopped. The brand settings app uses this window for everyone, including
 * system admins sitting in it; tighter operational windows stay in admin.
 */
export const HEALTHY_SYNC_WINDOW_DAYS = 10

/** True when `date` is inside the generous window ending at `now`. */
export function is_recent_sync(date: Date, now: Date = new Date()): boolean {
  return now.getTime() - date.getTime() <= HEALTHY_SYNC_WINDOW_DAYS * 24 * 60 * 60 * 1000
}

/**
 * Connections that store credentials for on-demand lookup and never run a scheduled sync.
 * There is no job to be "behind", so the settings UI treats them as healthy once connected.
 */
export function is_on_demand_connection(integrationName: string | undefined): boolean {
  return integrationName === "sps"
}

/**
 * Error fragments that mean the *stored credentials* no longer work, so syncing cannot resume
 * until someone signs in again. Only the brand can fix these, which is why they are the one class
 * of sync error we show them (see `syncStatusDetail` / `syncStatus`).
 *
 * Kept tight on purpose. `invalid_grant` covers Google's revoked/expired refresh tokens and every
 * Entra `AADSTS…` grant failure, which arrive wrapped in it. Deliberately *not* matched:
 * `HTTP 403`, which in practice is an S3 `AccessDenied` (our infrastructure, not their sign-in),
 * a portal's "Invalid username or password", or Graph's `ErrorSendAsDenied` — a re-connect fixes
 * none of them, and a false "Reconnect required" trains brands to ignore the one that is real.
 */
const AUTH_FAILURE_PATTERNS = [/invalid_grant/i, /invalid_client/i, /invalid_token/i, /InvalidAuthenticationToken/i, /\bHTTP 401\b/]

/** Shown to the brand in place of the usual reassurance, because they are the only ones who can act. */
export const AUTH_FAILURE_LABEL = "Reconnect required"

/** True when a sync failed because the connection's credentials were rejected. */
export function is_auth_failure(error: string | null | undefined): boolean {
  if (!error) return false
  return AUTH_FAILURE_PATTERNS.some(pattern => pattern.test(error))
}
